import SectionEmphasis from "./SectionEmphasis";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, CalendarDays, Check, MessageCircle, Workflow } from "lucide-react";
import { walkthroughExamples } from "./contextWalkthroughData";
import "./introducing-dibs.css";

const example = walkthroughExamples[0];
const clamp = (value: number) => Math.max(0, Math.min(1, value));
const finalHold = 0.2;
const ramp = (value: number, start: number, end: number) => clamp((value - start) / (end - start));

export default function IntroducingDibs() {
  const track = useRef<HTMLDivElement>(null);
  const composition = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [source, setSource] = useState<"history" | "timing" | null>(null);

  useEffect(() => {
    const rail = track.current;
    const surface = composition.current;
    if (!rail || !surface) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    const update = () => {
      frame = 0;
      // Measure heading and scene together so the entire pinned composition fits.
      const height = surface.offsetHeight;
      rail.style.setProperty("--intro-composition-height", `${height}px`);
      const mobile = window.innerWidth <= 900;
      // On phones the composition is taller than the visible viewport, so pin it by its bottom edge instead of falling back to static.
      const staticScene = reducedMotion.matches || (!mobile && height + 120 > window.innerHeight);
      rail.dataset.static = String(staticScene);
      const parsedTop = parseFloat(getComputedStyle(surface).top);
      const top = Number.isNaN(parsedTop) ? 104 : parsedTop;
      const travel = rail.offsetHeight - height;
      // The final draft rests for the last share of the pinned scroll before the page moves on.
      const progress = staticScene ? 1 : clamp((top - rail.getBoundingClientRect().top) / Math.max(1, travel) / (1 - finalHold));
      const assembly = ramp(progress, 0.05, 0.34);
      const draftStart = mobile ? 0.58 : 0.74;
      const draftEnd = mobile ? 0.88 : 0.94;
      const draft = ramp(progress, draftStart, draftEnd);
      const context = ramp(progress, 0.27, 0.36) * (1 - ramp(progress, draftStart - .08, draftStart));
      rail.style.setProperty("--intro-assembly", String(assembly));
      rail.style.setProperty("--intro-draft", String(draft));
      rail.style.setProperty("--intro-draft-opacity", String(ramp(progress, draftStart, draftStart + .03)));
      rail.style.setProperty("--intro-context", String(context));
      rail.style.setProperty("--intro-checks", String(ramp(progress, 0.36, 0.64)));
      rail.style.setProperty("--intro-sources", String(1 - draft));
      rail.dataset.scVerifyState = `${assembly.toFixed(3)}:${draft.toFixed(3)}:${context.toFixed(3)}`;
      rail.dataset.scVerifyHold = String(progress < 0.05 || (progress >= 0.36 && progress <= draftStart - .08) || progress > draftEnd);
      setStep(progress < 0.27 ? 0 : progress < draftEnd ? 1 : 2);
      if (progress < draftEnd) setSource(null);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new ResizeObserver(schedule);
    observer.observe(rail);
    observer.observe(surface);
    observer.observe(document.documentElement);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    reducedMotion.addEventListener("change", schedule);
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      reducedMotion.removeEventListener("change", schedule);
    };
  }, []);

  return (
    <section id="introducing-dibs" className="dv-intro" aria-labelledby="introducing-heading">
      <div className="dv-container">
        <div ref={track} className="dv-intro-track" data-step={step} data-source={source ?? "none"}>
          <div ref={composition} className="dv-intro-experience">
            <header className="dv-intro-heading">
              <h2 id="introducing-heading">Introducing <SectionEmphasis mark="brackets" tone="mint">Dibs</SectionEmphasis></h2>
              <p><span>Turn the context you already have</span> <span>into a more thoughtful next conversation.</span></p>
            </header>
          <div className="dv-intro-scene" role="group" aria-label="Customer details come together into a thoughtful follow-up">
            <div className="dv-intro-ground" aria-hidden="true" />
            <div className="dv-intro-record" aria-hidden={step === 0}>
              <div className="dv-intro-identity"><span className="dv-intro-avatar">{example.initials}</span><div><strong>{example.name}</strong><span>Profile context</span></div></div>
            </div>
            <div className="dv-intro-sources" aria-label="The source details">
              <div className="dv-intro-source dv-intro-source-timing" data-source-card="timing">
                <span className="dv-intro-source-label"><MessageCircle size={15} aria-hidden="true" />Past conversation</span>
                <p>“Mornings before work are easiest for me.”</p>
              </div>
              <div className="dv-intro-source dv-intro-source-history" data-source-card="history">
                <span className="dv-intro-source-label"><CalendarDays size={15} aria-hidden="true" />Visit history</span>
                <p>Last check-up: <strong>five months ago</strong></p>
              </div>
              <div className="dv-intro-source dv-intro-source-rule">
                <span className="dv-intro-source-label"><Workflow size={15} aria-hidden="true" />Your follow-up rule</span>
                <p>After five months, invite them to rebook.</p>
              </div>
            </div>
            <div className="dv-intro-context-note" aria-hidden={step !== 1}>
              <ul>
                {["The right context.", "The right timing.", "A relevant reason to reach out."].map((item, index) => (
                  <li key={item} className={index === 2 ? "is-final" : undefined}>
                    <span className="dv-intro-check"><Check size={14} strokeWidth={3.2} aria-hidden="true" /></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <span className="dv-intro-connection" aria-hidden="true"><ArrowRight size={22} strokeWidth={3} /></span>
            <div className="dv-intro-draft" aria-hidden={step !== 2}>
              <div className="dv-intro-draft-heading"><span><MessageCircle size={16} aria-hidden="true" />Suggested follow-up</span><span>Draft</span></div>
              <p>Hi Priscilla, it’s been a little while since your <button type="button" tabIndex={step === 2 ? 0 : -1} aria-pressed={source === "history"} onClick={() => setSource(source === "history" ? null : "history")}>last check-up</button>. Would a <button type="button" tabIndex={step === 2 ? 0 : -1} aria-pressed={source === "timing"} onClick={() => setSource(source === "timing" ? null : "timing")}>weekday morning</button> work for your next visit?</p>
              <div className="dv-intro-provenance" role="status">{source === "timing" ? "From her conversation: mornings before work are easiest." : source === "history" ? "From visit history: her last check-up was five months ago." : "Select an underlined phrase to see its source."}</div>
            </div>
          </div>
          </div>
        </div>
        <div className="dv-intro-caption-dwell"><div className="dv-intro-caption"><h3>Dibs is the teammate who never forgets</h3><p>It remembers everyone you serve and reaches out at the right moment, so people feel known and keep coming back.</p></div></div>
      </div>
    </section>
  );
}
